import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { fetchCurrentUser, login as apiLogin, register as apiRegister, logout as apiLogout } from '../lib/api';
import { logger } from '../utils/logger';

const AuthContext = createContext({
  user: null,
  loading: false,
  isAuthenticated: false,
  login: async () => {},
  register: async () => {},
  logout: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async (preserveExisting = false) => {
    try {
      // Check if we have a token in localStorage (fallback for when cookies are blocked)
      const storedToken = localStorage.getItem('auth_token');
      if (storedToken) {
        logger.debug('📦 Found token in localStorage, will use it for authentication');
      }
      
      const current = await fetchCurrentUser();
      // Only update user if we got a valid user, or if we're not preserving existing
      if (current || !preserveExisting) {
        setUser(current);
      }
      return current;
    } catch (error) {
      // Only clear user if we're not preserving existing user
      if (!preserveExisting) {
        setUser(null);
      }
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  const login = useCallback(async (email, password) => {
    try {
      const response = await apiLogin(email, password);
      logger.debug('Login response keys:', Object.keys(response || {}));
      
      // Store token in localStorage as fallback (in case cookies are blocked)
      // extractData returns response.data.data, so token should be at response.token
      const token = response?.token;
      if (token) {
        localStorage.setItem('auth_token', token);
        logger.debug('✅ Token stored in localStorage as fallback:', token.substring(0, 20) + '...');
      } else {
        logger.error('❌ No token in login response!', {
          hasToken: !!response?.token,
          keys: Object.keys(response || {})
        });
      }
      
      // Set user immediately from response if available
      const loginUser = response?.user;
      if (loginUser) {
        setUser(loginUser);
        setLoading(false);
        logger.debug('✅ User set from login response:', loginUser);
      }
      
      // Wait for cookie to be set by browser
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Try to reload user to verify cookie is working
      const loadedUser = await loadUser(true); // preserveExisting = true
      logger.debug('User loaded after login:', loadedUser ? '✅ Cookie working' : '❌ Cookie not working - using token fallback');
      
      // Use loaded user if available, otherwise keep the login response user
      const finalUser = loadedUser || loginUser;
      if (finalUser) {
        setUser(finalUser);
        setLoading(false);
        logger.debug('Final user set:', finalUser);
      } else {
        logger.warn('⚠️ No user available after login!');
      }
      
      return { success: true, user: finalUser };
    } catch (error) {
      logger.error('Login error:', error);
      localStorage.removeItem('auth_token'); // Clear token on error
      setUser(null);
      setLoading(false);
      return {
        success: false,
        error: error.response?.data?.message || 'Login failed. Please try again.',
      };
    }
  }, [loadUser]);

  const register = useCallback(async (userData) => {
    try {
      const response = await apiRegister(userData);
      logger.debug('Register response received');
      
      // Set user immediately from response if available
      const registerUser = response?.user;
      if (registerUser) {
        setUser(registerUser);
        setLoading(false);
        logger.debug('User set from register response:', registerUser);
      }
      
      // Wait a bit for cookie to be set by browser
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Try to reload user to verify cookie is working, but preserve existing user if cookie fails
      const loadedUser = await loadUser(true); // preserveExisting = true
      logger.debug('User loaded after register:', loadedUser);
      
      // Use loaded user if available, otherwise keep the register response user
      const finalUser = loadedUser || registerUser;
      if (finalUser) {
        setUser(finalUser);
        setLoading(false);
      }
      
      return { success: true, user: finalUser };
    } catch (error) {
      logger.error('Register error:', error);
      setUser(null);
      setLoading(false);
      return {
        success: false,
        error: error.response?.data?.message || 'Registration failed. Please try again.',
      };
    }
  }, [loadUser]);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
      localStorage.removeItem('auth_token'); // Clear token fallback
      setUser(null);
      return { success: true };
    } catch (error) {
      localStorage.removeItem('auth_token'); // Clear token fallback even on error
      setUser(null);
      return { success: true };
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated: Boolean(user),
      login,
      register,
      logout,
    }),
    [user, loading, login, register, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
